<script setup>
// Imports
import { format } from 'quasar'

// Constants
const api = useFeathers()
const { capitalize, humanStorageSize } = format
const { t } = useI18n({ useScope: 'global' })

// Component properties
const props = defineProps({
  label: {
    type: String,
    required: false,
    default: undefined,
  },
  objectReference: {
    type: Object,
    required: false,
    default: undefined,
  },
  showSaveButton: {
    type: Boolean,
    required: false,
    default: false,
  },
  multiple: {
    type: Boolean,
    default: true,
  },
  accept: {
    type: String,
    required: false,
    default: '',
  },
  clearable: {
    type: Boolean,
    required: false,
    default: false,
  },
  maxFileSize: {
    type: Number,
    required: false,
    default: undefined,
  },
  error: {
    type: Boolean,
    required: false,
    default: false,
  },
  errorMessage: {
    type: String,
    required: false,
    default: undefined,
  },
  maxTotalSize: {
    type: Number,
    required: false,
    default: undefined,
  },
  readonly: {
    type: Boolean,
    required: false,
    default: false,
  },
  disable: {
    type: Boolean,
    required: false,
    default: false,
  },

  // Used when parent provides dataUrl without filename
  defaultFilename: {
    type: String,
    required: false,
    default: 'upload',
  },
})

const emit = defineEmits(['uploadedFileIds'])

// Models
const base64Model = defineModel({
  type: [Array, Object, null],
  default: () => [],
})

// Refs
const files = ref(null)
const isClearing = ref(false)

const errorRef = ref(props.error)
const errorMessageRef = ref(props.errorMessage)

// Computed properties
const hasError = computed(() => {
  return props.error || errorRef.value
})

const errorMessageModel = computed(() => {
  return errorMessageRef.value ?? props.errorMessage
})

const hasFiles = computed(() => {
  if (!files.value) {
    return false
  }
  return Array.isArray(files.value) ? files.value.length > 0 : true
})

const normalizedFiles = computed(() => {
  if (!files.value) {
    return []
  }
  return Array.isArray(files.value) ? files.value : [files.value]
})

const isDisabled = computed(() => props.disable || props.readonly)

const acceptResolved = computed(() => {
  const raw = typeof props.accept === 'string' ? props.accept : ''

  const tokens = raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)

  const hasGzip = tokens.includes('application/gzip')
  const hasXGzip = tokens.includes('application/x-gzip')

  if (hasGzip && !hasXGzip) {
    tokens.push('application/x-gzip')
  }

  return tokens.join(',')
})

const normalizedModel = computed(() => {
  return normalizeModelToArray(base64Model.value)
})

const currentModelDisplayValue = computed(() => {
  if (hasFiles.value) {
    return undefined
  }

  const list = normalizeModelToArray(base64Model.value)
  if (!list.length) {
    return undefined
  }

  const names = list
    .map((v) => {
      const filename = v?.filename
      if (typeof filename === 'string' && filename.trim().length > 0) {
        return filename.trim()
      }

      const name = v?.name
      if (typeof name === 'string' && name.trim().length > 0) {
        return name.trim()
      }

      return null
    })
    .filter((v) => typeof v === 'string' && v.trim().length > 0)

  if (!names.length) {
    return undefined
  }

  if (props.multiple && names.length > 1) {
    return `${names[0]} (+${names.length - 1})`
  }

  return names[0]
})

// Methods
const normalizeModelToArray = (val) => {
  if (!val) {
    return []
  }
  if (Array.isArray(val)) {
    return val.filter(Boolean)
  }
  if (typeof val === 'object') {
    return [val]
  }
  return []
}

const readFileAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })

const buildPayloadFromFiles = async () => {
  if (!hasFiles.value) {
    return []
  }

  const payload = []
  let i = 0

  for (const file of normalizedFiles.value) {
    i += 1
    const dataUrl = await readFileAsDataURL(file)

    payload.push({
      name: file.name || (props.multiple ? `${props.defaultFilename}-${i}` : props.defaultFilename),
      dataUrl,
    })
  }

  return payload
}

const applyPayloadToModel = (payloadArr) => {
  const payload = (payloadArr || []).filter(Boolean).map((item, i) => ({
    name: item.name || (props.multiple ? `${props.defaultFilename}-${i + 1}` : props.defaultFilename),
    dataUrl: item.dataUrl,
  }))

  base64Model.value = props.multiple ? payload : (payload[0] ?? null)
}

const refresh = async () => {
  const payload = await buildPayloadFromFiles()
  applyPayloadToModel(payload)
}

const clear = async (resetModelValue = false) => {
  if (isClearing.value) {
    return
  }
  isClearing.value = true

  files.value = null

  await nextTick()
  clearErrors()
  isClearing.value = false
}

const onQFileClear = async () => {
  if (isClearing.value) {
    return
  }
  await clear(false)
}

const uploadFile = async () => {
  const uploadedFileIds = []

  try {
    if (hasFiles.value) {
      await refresh()
    }

    for (const item of normalizedModel.value) {
      const response = await api.service('files').create({
        metadata: {
          removable: true,
          objectReference: props.objectReference,
        },
        filename: item.name,
        buffer: item.dataUrl,
      })
      uploadedFileIds.push(response._id)
    }
  } catch (e) {
    console.error(e)
  } finally {
    if (uploadedFileIds.length) {
      emit('uploadedFileIds', uploadedFileIds)
      await clear(true)
    }
  }
}

const setPayload = (payload) => {
  base64Model.value = payload
}

const onRejected = (error) => {
  errorRef.value = true
  errorMessageRef.value = t('Shared.Globals.Errors.FileUploader.' + capitalize(error[0]?.failedPropValidation), {
    maxMB: humanStorageSize(props.maxTotalSize ?? props.maxFileSize, 0),
  })
}

const clearErrors = () => {
  errorRef.value = false
  errorMessageRef.value = null
}

// Watchers
watch(
  () => files.value,
  async () => {
    if (isClearing.value) {
      return
    }

    if (hasFiles.value) {
      await refresh()
    }
  },
)

watch(
  () => props.multiple,
  () => {
    applyPayloadToModel(normalizedModel.value)
  },
)

// Exposes
defineExpose({
  getPayload: () => base64Model.value,
  refresh,
  clear,
  uploadFile,
  setPayload,
})
</script>

<template>
  <div class="full-width file-uploader">
    <q-file
      v-model="files"
      input-class="file-uploader__display-text ellipsis text-no-wrap overflow-hidden"
      input-style="display: block; flex: 1 1 0%; min-width: 0; width: 0; max-width: 100%;"
      class="file-uploader__field full-width overflow-hidden"
      outlined
      :label="label"
      :multiple="multiple"
      :accept="acceptResolved"
      :clearable="clearable"
      :max-file-size="maxFileSize"
      :max-total-size="maxTotalSize"
      :disable="isDisabled"
      :error="hasError"
      :error-message="errorMessageModel"
      :display-value="currentModelDisplayValue"
      @clear="onQFileClear"
      @rejected="onRejected"
      @update:model-value="clearErrors"
      v-bind="$attrs"
    >
      <template #prepend>
        <q-icon name="attach_file" />
      </template>

      <template v-if="showSaveButton" #append>
        <q-btn push icon="save" color="primary" :disable="isDisabled || (normalizedModel.length === 0 && !hasFiles)" @click="uploadFile" />
      </template>

      <template v-for="(_, name) in $slots" #[name]="slotData">
        <slot :name="name" :files="files" :payload="base64Model" :clear="clear" :refresh="refresh" v-bind="slotData || {}"></slot>
      </template>
    </q-file>
    <span></span>
  </div>
</template>

<style scoped lang="scss">
.file-uploader {
  min-width: 0;
  max-width: 100%;
}

.file-uploader__field {
  min-width: 0;
  max-width: 100%;
}

.file-uploader :deep(.q-field__control-container),
.file-uploader :deep(.q-field__native) {
  flex: 1 1 0%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
}

.file-uploader :deep(.q-field__native),
.file-uploader :deep(.q-field__input) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-uploader :deep(.file-uploader__display-text) {
  flex: 1 1 0%;
  display: block;
  min-width: 0;
  width: 0;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.file-uploader :deep(.q-field__messages > div[role='alert']) {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
}
</style>
